import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs, onSnapshot, query, where, writeBatch } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/Toast";
import "../App.css";
import { signInAnonymously } from "firebase/auth";
import { FaShareAlt } from "react-icons/fa";
import ShareGroupModal from "./ShareGroupModal";
import { getShareToken } from "../services/sharing";
import { getSharingGroupByToken, calculateSharingGroupScope } from "../services/sharingGroups";

const MAX_IN_QUERY = 10;

const chunkIds = (ids) => {
  const chunks = [];
  for (let i = 0; i < ids.length; i += MAX_IN_QUERY) {
    chunks.push(ids.slice(i, i + MAX_IN_QUERY));
  }
  return chunks;
};

const GroupsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [racers, setRacers] = useState([]);
  const [sharedRuns, setSharedRuns] = useState([]);
  const [userData, setUserData] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergedGroupName, setMergedGroupName] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [groupName, setGroupName] = useState("");

  const shareGroupId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("id");
    if (value) {
      localStorage.setItem("guestShareGroupId", value);
      return value;
    }
    const cached = localStorage.getItem("guestShareGroupId");
    return cached || "";
  }, [location.search]);

  const shareToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token") || "";
  }, [location.search]);

  const shareGroupToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("shareGroupToken") || "";
  }, [location.search]);

  const [tokenData, setTokenData] = useState(null);
  const [tokenScope, setTokenScope] = useState(null);
  const [shareGroupData, setShareGroupData] = useState(null);
  const [shareGroupScope, setShareGroupScope] = useState(null);

  const isGuest = Boolean(user?.isAnonymous);
  const isGuestShare = Boolean(shareGroupId) && (isGuest || !user);
  const isTokenShare = Boolean(shareToken);
  const isShareGroupShare = Boolean(shareGroupToken);

  const requireAccount = () => {
    if (!user || user.isAnonymous) {
      showToast("Please sign in to edit or create content.");
      navigate("/login");
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!user) {
      setUserData(null);
      return;
    }
    const docRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserData(snapshot.data());
      }
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if ((!shareGroupId && !shareToken && !shareGroupToken) || user) return;
    signInAnonymously(auth).catch((error) => {
      console.error("Anonymous sign-in error:", error);
    });
  }, [shareGroupId, shareToken, shareGroupToken, user]);

  // Load token data when token is present
  useEffect(() => {
    if (!shareToken) {
      setTokenData(null);
      setTokenScope(null);
      return;
    }

    // Wait for user to be available (anonymous sign-in might be in progress)
    if (!user && shareToken) {
      // Sign in anonymously if needed
      signInAnonymously(auth).catch((error) => {
        console.error("Anonymous sign-in error:", error);
      });
      return; // Will retry when user is available
    }

    const loadToken = async () => {
      try {
        const data = await getShareToken(shareToken);
        if (data && !data.revoked) {
          setTokenData(data);
          setTokenScope(data.scope || { runs: [], racers: [], tracks: [], sessions: [], groups: [] });
        } else {
          setTokenData(null);
          setTokenScope(null);
        }
      } catch (error) {
        console.error("Error loading share token:", error);
        setTokenData(null);
        setTokenScope(null);
      }
    };

    loadToken();
  }, [shareToken, user]);

  // Load sharing group data when shareGroupToken is present
  useEffect(() => {
    if (!shareGroupToken) {
      setShareGroupData(null);
      setShareGroupScope(null);
      return;
    }

    // Wait for user to be available (anonymous sign-in might be in progress)
    if (!user && shareGroupToken) {
      signInAnonymously(auth).catch((error) => {
        console.error("Anonymous sign-in error:", error);
      });
      return; // Will retry when user is available
    }

    const loadSharingGroup = async () => {
      try {
        const groupData = await getSharingGroupByToken(shareGroupToken);
        if (groupData) {
          setShareGroupData(groupData);
          // Calculate scope for the sharing group
          const scope = await calculateSharingGroupScope(groupData.id);
          setShareGroupScope(scope);
        } else {
          setShareGroupData(null);
          setShareGroupScope(null);
        }
      } catch (error) {
        console.error("Error loading sharing group:", error);
        setShareGroupData(null);
        setShareGroupScope(null);
      }
    };

    loadSharingGroup();
  }, [shareGroupToken, user]);

  useEffect(() => {
    if (!shareGroupId || !user) return;
    const fetchGroupName = async () => {
      const groupDocRef = doc(db, "sharegroups", shareGroupId);
      const groupSnap = await getDoc(groupDocRef);
      if (groupSnap.exists()) {
        setGroupName(groupSnap.data().name || "");
      } else {
        setGroupName("");
      }
    };
    fetchGroupName();
  }, [shareGroupId, user]);

  useEffect(() => {
    if (!isGuestShare || !user || !user.isAnonymous) return;
    const runsMap = new Map();
    const unsubscribers = [];

    const upsertRuns = () => {
      setSharedRuns(Array.from(runsMap.values()));
    };

    const subscribeSharedRuns = (collectionName, filterField, filterValue) => {
      if (!filterValue) return;
      const runsRef = collection(db, collectionName);
      const q = query(runsRef, where(filterField, "==", filterValue));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docs.forEach((docSnap) => {
          runsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
        upsertRuns();
      });
      unsubscribers.push(unsubscribe);
    };

    subscribeSharedRuns("events", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("runs", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("events", "sharedGroupName", groupName);
    subscribeSharedRuns("runs", "sharedGroupName", groupName);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [groupName, isGuestShare, shareGroupId, user]);

  useEffect(() => {
    if (!user || user.isAnonymous || isGuestShare) {
      setRacers([]);
      return;
    }

    const racersRef = collection(db, "timer_racers");
    const unsubscribe = onSnapshot(
      racersRef,
      (snapshot) => {
        if (snapshot.empty) {
          setRacers([]);
          return;
        }
        const allRacers = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        const userRacers = allRacers.filter((racer) => {
          const hasCreatedBy = racer.createdBy !== undefined && racer.createdBy !== null;
          const matchesUser = racer.createdBy === user.uid;
          if (matchesUser) return true;
          if (!hasCreatedBy) return true;
          return false;
        });
        setRacers(userRacers);
      },
      (error) => {
        console.error("Error fetching racers:", error);
        setRacers([]);
      }
    );

    return () => unsubscribe();
  }, [isGuestShare, user]);

  useEffect(() => {
    const fetchGuestRacers = async () => {
      if (!isGuestShare || !user || !user.isAnonymous) {
        return;
      }

      const racerIds = new Set();
      sharedRuns.forEach((run) => {
        if (!Array.isArray(run.racers)) return;
        run.racers.forEach((entry) => {
          if (entry?.racerId) {
            racerIds.add(entry.racerId);
          }
        });
      });

      const uniqueIds = Array.from(racerIds);
      if (uniqueIds.length === 0) {
        setRacers([]);
        return;
      }

      const racersRef = collection(db, "timer_racers");
      const chunks = chunkIds(uniqueIds);
      const racerDocs = [];

      for (const chunk of chunks) {
        const q = query(racersRef, where("__name__", "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          racerDocs.push({ id: docSnap.id, ...docSnap.data() });
        });
      }

      setRacers(racerDocs);
    };

    fetchGuestRacers();
  }, [isGuestShare, sharedRuns, user]);

  // Fetch racers for token-based sharing
  useEffect(() => {
    const fetchTokenRacers = async () => {
      if (!isTokenShare || !tokenScope || !user) {
        return;
      }

      const accessibleRacerIds = new Set(tokenScope.racers || []);
      if (accessibleRacerIds.size === 0) {
        setRacers([]);
        return;
      }

      const racersRef = collection(db, "timer_racers");
      const racerIdsArray = Array.from(accessibleRacerIds);
      const chunks = chunkIds(racerIdsArray);
      const racerDocs = [];

      for (const chunk of chunks) {
        const q = query(racersRef, where("__name__", "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          racerDocs.push({ id: docSnap.id, ...docSnap.data() });
        });
      }

      setRacers(racerDocs);
    };

    fetchTokenRacers();
  }, [isTokenShare, tokenScope, user]);

  // Fetch racers for sharing group token
  useEffect(() => {
    const fetchShareGroupRacers = async () => {
      if (!isShareGroupShare || !shareGroupScope || !user) {
        return;
      }

      const accessibleRacerIds = new Set(shareGroupScope.racers || []);
      if (accessibleRacerIds.size === 0) {
        setRacers([]);
        return;
      }

      const racersRef = collection(db, "timer_racers");
      const racerIdsArray = Array.from(accessibleRacerIds);
      const chunks = chunkIds(racerIdsArray);
      const racerDocs = [];

      for (const chunk of chunks) {
        const q = query(racersRef, where("__name__", "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          racerDocs.push({ id: docSnap.id, ...docSnap.data() });
        });
      }

      setRacers(racerDocs);
    };

    fetchShareGroupRacers();
  }, [isShareGroupShare, shareGroupScope, user]);

  const isAdmin = Boolean(userData?.isAdmin);

  const groupsWithCounts = useMemo(() => {
    const counts = new Map();
    racers.forEach((racer) => {
      const groups = Array.isArray(racer.groups) ? racer.groups : [];
      const uniqueGroups = new Set(groups);
      uniqueGroups.forEach((group) => {
        counts.set(group, (counts.get(group) || 0) + 1);
      });
    });
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [racers]);

  const handleToggleGroupSelection = (groupName) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedGroups.size === groupsWithCounts.length) {
      setSelectedGroups(new Set());
    } else {
      setSelectedGroups(new Set(groupsWithCounts.map((group) => group.name)));
    }
  };

  const handleOpenMergeModal = () => {
    if (!requireAccount()) return;
    if (selectedGroups.size < 2) {
      showToast("Please select at least 2 groups to merge.");
      return;
    }
    setMergedGroupName(Array.from(selectedGroups).join(" / "));
    setShowMergeModal(true);
  };

  const handleCloseMergeModal = () => {
    setShowMergeModal(false);
    setMergedGroupName("");
  };

  const handleConfirmMerge = async () => {
    if (!requireAccount()) return;
    const targetGroup = mergedGroupName.trim();
    if (!targetGroup) {
      showToast("Please enter a name for the merged group.");
      return;
    }
    if (selectedGroups.size < 2) {
      showToast("Please select at least 2 groups to merge.");
      return;
    }

    try {
      const selectedGroupList = Array.from(selectedGroups);
      const selectedSet = new Set(selectedGroupList);

      const racersToUpdate = racers.filter((racer) => {
        const groups = Array.isArray(racer.groups) ? racer.groups : [];
        return groups.some((group) => selectedSet.has(group));
      });

      const batch = writeBatch(db);
      racersToUpdate.forEach((racer) => {
        const existingGroups = Array.isArray(racer.groups) ? racer.groups : [];
        const hasSelectedGroup = existingGroups.some((group) => selectedSet.has(group));
        if (!hasSelectedGroup) {
          return;
        }

        const updatedGroups = existingGroups.filter((group) => {
          if (!selectedSet.has(group)) return true;
          return group === targetGroup;
        });

        if (!updatedGroups.includes(targetGroup)) {
          updatedGroups.push(targetGroup);
        }

        const dedupedGroups = Array.from(new Set(updatedGroups));
        batch.update(doc(db, "timer_racers", racer.id), { groups: dedupedGroups });
      });

      await batch.commit();

      setSelectedGroups(new Set());
      handleCloseMergeModal();
      showToast(`Merged ${selectedGroupList.length} groups into "${targetGroup}".`);
    } catch (error) {
      console.error("Error merging groups:", error);
      showToast("Failed to merge groups. Please try again.");
    }
  };

  return (
    <div style={{ width: "100%", padding: "30px 20px", height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box", maxWidth: "100%" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div className="homepage-other-tools" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <h1>Groups</h1>

          <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
            {!isGuestShare && !isTokenShare && !isShareGroupShare && user && !user.isAnonymous && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <input
                    type="checkbox"
                    checked={groupsWithCounts.length > 0 && selectedGroups.size === groupsWithCounts.length}
                    onChange={handleSelectAll}
                    style={{ cursor: "pointer" }}
                    id="selectAllGroupsCheckbox"
                  />
                  <label htmlFor="selectAllGroupsCheckbox" style={{ cursor: "pointer" }}>
                    Select All
                  </label>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, justifyContent: "flex-end" }}>
                  {selectedGroups.size > 0 && (
                    <span style={{ fontSize: "0.9em", color: "#555" }}>
                      {selectedGroups.size} group{selectedGroups.size !== 1 ? "s" : ""} selected
                    </span>
                  )}
                  {isAdmin && (
                  <button
                    onClick={handleOpenMergeModal}
                    disabled={selectedGroups.size < 2}
                    style={{
                      padding: "8px 20px",
                      backgroundColor: selectedGroups.size >= 2 ? "#e67e22" : "#ccc",
                      border: "none",
                      color: "#fff",
                      cursor: selectedGroups.size >= 2 ? "pointer" : "not-allowed",
                      borderRadius: "5px",
                      fontWeight: "bold",
                      opacity: selectedGroups.size >= 2 ? 1 : 0.6,
                    }}
                  >
                    Merge Groups ({selectedGroups.size})
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (selectedGroups.size === 0) {
                        showToast("Please select at least one group to share.");
                        return;
                      }
                      setShowShareModal(true);
                    }}
                    disabled={selectedGroups.size === 0}
                    style={{
                      padding: "8px 20px",
                      backgroundColor: selectedGroups.size > 0 ? "#28a745" : "#ccc",
                      border: "none",
                      color: "#fff",
                      cursor: selectedGroups.size > 0 ? "pointer" : "not-allowed",
                      borderRadius: "5px",
                      fontWeight: "bold",
                      opacity: selectedGroups.size > 0 ? 1 : 0.6,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                  >
                    <FaShareAlt /> Share ({selectedGroups.size})
                  </button>
                  {selectedGroups.size > 0 && (
                    <button
                      onClick={() => setSelectedGroups(new Set())}
                      style={{
                        padding: "8px 15px",
                        backgroundColor: "#999",
                        border: "none",
                        color: "#fff",
                        cursor: "pointer",
                        borderRadius: "5px",
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              border: "1px solid #ccc",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #ccc" }}>
                  {!isGuestShare && !isTokenShare && !isShareGroupShare && user && !user.isAnonymous && (
                    <th style={{ padding: "8px", backgroundColor: "#f5f5f5", width: "50px" }}></th>
                  )}
                  <th style={{ textAlign: "left", padding: "8px", backgroundColor: "#f5f5f5" }}>Group</th>
                  <th style={{ textAlign: "center", padding: "8px", backgroundColor: "#f5f5f5" }}>Members</th>
                </tr>
              </thead>
              <tbody>
                {groupsWithCounts.length > 0 ? (
                  groupsWithCounts.map((group) => (
                    <tr
                      key={group.name}
                      style={{
                        borderBottom: "1px solid #eee",
                        backgroundColor: selectedGroups.has(group.name) ? "#f0f8ff" : "transparent",
                      }}
                    >
                      {!isGuestShare && !isTokenShare && !isShareGroupShare && user && !user.isAnonymous && (
                        <td style={{ padding: "8px", textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={selectedGroups.has(group.name)}
                            onChange={() => handleToggleGroupSelection(group.name)}
                            style={{ cursor: "pointer" }}
                            title="Select group"
                          />
                        </td>
                      )}
                      <td style={{ padding: "8px" }}>{group.name}</td>
                      <td style={{ padding: "8px", textAlign: "center" }}>{group.count}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={isAdmin ? "3" : "2"} style={{ textAlign: "center", padding: "10px" }}>
                      No groups found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showMergeModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={handleCloseMergeModal}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "30px",
              width: "90%",
              maxWidth: "500px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: "20px" }}>Merge Groups</h2>
            <p style={{ marginBottom: "20px", color: "#666" }}>
              This will assign all racers from the selected groups to the new group name.
            </p>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
                New Group Name:
              </label>
              <input
                type="text"
                value={mergedGroupName}
                onChange={(e) => setMergedGroupName(e.target.value)}
                placeholder="Enter merged group name"
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
                autoFocus
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={handleCloseMergeModal}
                style={{
                  padding: "8px 20px",
                  backgroundColor: "#999",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                  fontWeight: "bold",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMerge}
                style={{
                  padding: "8px 20px",
                  backgroundColor: "#e67e22",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  borderRadius: "5px",
                  fontWeight: "bold",
                }}
              >
                Merge Groups
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Group Modal */}
      <ShareGroupModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
        }}
        resources={Array.from(selectedGroups).map(groupName => ({
          type: 'group',
          id: groupName, // Groups are identified by name
          name: groupName
        }))}
        onShareComplete={() => {
          setShowShareModal(false);
        }}
      />
    </div>
  );
};

export default GroupsList;
